import { gql } from "@apollo/client";
import { UserId } from "@hex/common";
import React, { useCallback } from "react";
import styled from "styled-components";

import { useToggleState } from "../../hooks/useToggleState";
import { CyData } from "../../util/cypress";

import { CommentActions } from "./CommentActions";
import { CommentAuthor } from "./CommentAuthor";
import { CommentEditor } from "./CommentEditor";
import { CommentReactionButton } from "./CommentReactionButton";
import { CommentReactionList } from "./CommentReactionList";
import { CommentText } from "./CommentText";
import { CommentStub } from "./CommentThread";

gql`
  query CommentUserInfo($userId: UserId!) {
    userById(userId: $userId) {
      id
      name
      email
      imageUrl
    }
  }
`;

const CommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: 100%;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 1px;
`;

const CommentBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

export const ChildCommentActionsContainerDiv = styled.div`
  display: flex;
  flex: none;
  gap: 5px;
  padding-left: 5px;

  transition: opacity ${({ theme }) => theme.animation.duration}
    ${({ theme }) => theme.animation.easing};
`;

export type CommentViewLocation = "project" | "app";

export interface CommentProps {
  readonly appComment: boolean;
  readonly comment: CommentStub;
  readonly editable: boolean;
  readonly currentUserId: UserId | undefined;
  readonly setIsActionsOpen: (isOpen: boolean) => void;
}

export const Comment: React.ComponentType<CommentProps> = React.memo(
  function Comment({
    appComment,
    comment,
    currentUserId,
    editable,
    setIsActionsOpen,
  }) {
    const {
      commentId,
      createdDate,
      creatorId,
      editedTextDate,
      entityId,
      entityType,
      reactions,
      richText,
    } = comment;
    const [isEditing, , { setFalse: setNotEditing, setTrue: setEditing }] =
      useToggleState(false);
    const onActionsMenuOpened = useCallback(
      () => setIsActionsOpen(true),
      [setIsActionsOpen],
    );
    const onActionsMenuClosed = useCallback(
      () => setIsActionsOpen(false),
      [setIsActionsOpen],
    );

    return (
      <CommentWrapper>
        <CommentHeader>
          <CommentAuthor
            createdDate={createdDate}
            creatorId={creatorId}
            editedTextDate={editedTextDate}
          />
          <ChildCommentActionsContainerDiv>
            <CommentReactionButton
              commentId={commentId}
              reactions={reactions}
              onToggle={setIsActionsOpen}
            />
            <CommentActions
              commentEditable={creatorId === currentUserId && editable}
              commentId={commentId}
              setEditing={setEditing}
              onMenuClosed={onActionsMenuClosed}
              onMenuOpened={onActionsMenuOpened}
            />
          </ChildCommentActionsContainerDiv>
        </CommentHeader>
        <CommentBody data-cy={CyData.COMMENT_BODY}>
          {isEditing ? (
            <CommentEditor
              appComment={appComment}
              commentId={commentId}
              creatorId={creatorId}
              entityId={entityId}
              entityType={entityType}
              helperText="Save"
              initialRichText={richText}
              onComplete={setNotEditing}
            />
          ) : (
            <CommentText commentRichText={richText} />
          )}
        </CommentBody>
        {reactions.length > 0 && (
          <CommentReactionList commentId={commentId} reactions={reactions} />
        )}
      </CommentWrapper>
    );
  },
);
